import { App } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getApiError, getMarketV2Info } from "tm-shared/api";
import { MarketV2 } from "tm-shared/types";

import { useProtectedUserRoleSelector } from "@/hooks/redux";
import NotFound from "@/pages/System/NotFound";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { BlockList } from "@/shared/pageComposition/BlockList";
import { BlockLoader } from "@/shared/ui/BlockLoader";

import {
    getMarketVerifyAddressBlocks,
    MarketVerifyAddressModel,
} from "./MarketVerifyAddress.blocks";
import { getMarketVerifyAddressCapabilities } from "./MarketVerifyAddress.policy";

export const MarketVerifyAddress = () => {
    const role = useProtectedUserRoleSelector();
    const { marketId = "", pointId = "" } = useParams();
    const { notification } = App.useApp();

    const [market, setMarket] = useState<MarketV2 | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchMarket = async () => {
            try {
                if (!marketId) {
                    throw new Error("id маркету відсутній");
                }

                setLoading(true);

                const response = await getMarketV2Info({ marketId });

                setMarket(response.result);
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    title: "Не вдалося завантажити маркет",
                    description: displayMessage,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchMarket();
    }, [marketId, notification]);

    const model: MarketVerifyAddressModel | null = useMemo(() => {
        if (!market) return null;
        return {
            capabilities: getMarketVerifyAddressCapabilities(role),
            market,
            pointId,
        };
    }, [market, role, pointId]);

    const blocks = useMemo(
        () => (model ? getMarketVerifyAddressBlocks(model) : []),
        [model],
    );

    if (loading) {
        return (
            <>
                <Header back title="Верифікація адреси" />
                <MainContainer>
                    <BlockLoader />
                </MainContainer>
            </>
        );
    }

    if (!market || !!market?.building.address?.id || !model) {
        return <NotFound />;
    }

    return (
        <>
            <Header back title="Верифікація адреси" />
            <MainContainer>
                <BlockList blocks={blocks} model={model} />
            </MainContainer>
        </>
    );
};

export default MarketVerifyAddress;
